import React from 'react';
import { Box, type BoxProps } from '@mui/material';

export interface NotepadBoxProps extends BoxProps {
  /** Content to display inside the notepad */
  children: React.ReactNode;
  /** Distance from left edge to red margin line (default: 35px) */
  marginLinePosition?: string;
  /** Padding left to add space between margin line and content (default: 4.5 = 36px) */
  contentPaddingLeft?: number;
  /** Padding top and bottom for the inner content box (default: 3) */
  contentPaddingY?: number;
}

/**
 * NotepadBox - A reusable component that displays content with a notepad aesthetic
 * Features:
 * - Yellow/cream background
 * - Horizontal lines (lined paper effect)
 * - Red margin line on the left
 * - Rounded rectangle card styling
 */
export const NotepadBox: React.FC<NotepadBoxProps> = ({
  children,
  marginLinePosition = '35px',
  contentPaddingLeft = 4.5,
  contentPaddingY = 3,
  sx,
  ...props
}) => {
  return (
    <Box
      sx={{
        bgcolor: '#fef9e7',
        border: '0.5px solid',
        borderColor: 'borderColors.secondary',
        borderRadius: 3,
        boxShadow: 'none',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            'repeating-linear-gradient(transparent, transparent 31px, rgba(0,0,0,0.05) 31px, rgba(0,0,0,0.05) 32px)',
          pointerEvents: 'none',
          opacity: 0.4,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: marginLinePosition,
          bottom: 0,
          width: '2px',
          background: '#ff6b6b',
          opacity: 0.3,
        },
        ...sx,
      }}
      {...props}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          pl: contentPaddingLeft,
          pr: 3,
          py: contentPaddingY,
          height: contentPaddingY === 0 ? '100%' : 'auto',
          display: contentPaddingY === 0 ? 'flex' : 'block',
          flexDirection: contentPaddingY === 0 ? 'column' : undefined,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

